import React, { useState } from 'react';
import { useUser } from '@/context/UserContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import { VoxelTypeKey, RarityKey } from '@/types';
import '../styles/Stake.css';

const VOXEL_COLORS: Record<string, string> = {
    grass: '#ffd1cc',
    dirt: '#ffffcc',
    stone: '#cceeff',
    coal_ore: '#ccffcc',
    iron_ore: '#e6ccff',
    tree_top: '#ffcccc',
    leave: '#ffe6cc',
    sand: '#f2f2f2',
    cloud: '#f0f0f0',
    snow: '#ccffff',
    jungle_tree: '#ffffe6',
    jungle_leaves: '#ccccff',
    cactus: '#ffebcc',
    jungle_grass: '#e6ffff',
    lava: '#fce6ff',
    frost_mint: '#e6ffe6'
};

const StakeCard = () => {
    const { user, voxelInventory } = useUser();
    const [loading, setLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState<'stake' | 'unstake'>('stake');

    const stakeBlocks = httpsCallable(functions, 'stakeBlocks');
    const unstakeBlocks = httpsCallable(functions, 'unstakeBlocks');

    const handleAction = async (type: VoxelTypeKey, rarity: RarityKey, amount: number, action: 'stake' | 'unstake') => {
        setLoading(true);
        try {
            const result = action === 'stake'
                ? await stakeBlocks({ type, rarity, amount })
                : await unstakeBlocks({ type, rarity, amount });

            if ((result.data as any).success) {
                console.log(`${action} successful`);
            }
        } catch (e: any) {
            console.error(e);
            alert(`Failed to ${action}: ` + (e.message || "Unknown error"));
        } finally {
            setLoading(false);
        }
    };

    const renderVoxelList = (section: 'available' | 'staked', action: 'stake' | 'unstake') => {
        if (!voxelInventory || !voxelInventory[section]) return <p className="empty-msg">No blocks available.</p>;

        const rarities: RarityKey[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
        const items: React.JSX.Element[] = [];

        rarities.forEach(rarity => {
            const voxels = voxelInventory[section][rarity];
            if (!voxels) return;

            Object.entries(voxels).forEach(([type, countValue]) => {
                const count = countValue as number;
                if (count > 0) {
                    items.push(
                        <div key={`${rarity}-${type}`} className={`staking-item ${rarity}`}>
                            <div className="item-info">
                                <div className="voxel-preview" style={{ backgroundColor: VOXEL_COLORS[type] }}></div>
                                <span>{rarity.charAt(0).toUpperCase() + rarity.slice(1)} {type.replace('_', ' ')} ({count})</span>
                            </div>
                            <button
                                onClick={() => handleAction(type as VoxelTypeKey, rarity, 1, action)}
                                disabled={loading}
                            >
                                {loading ? '...' : (action === 'stake' ? 'Stake' : 'Unstake')}
                            </button>
                        </div>
                    );
                }
            });
        });

        return items.length > 0 ? (
            <div className="staking-list">
                {items}
            </div>
        ) : (
            <p className="empty-msg">No blocks {section === 'staked' ? 'staked' : 'available'}.</p>
        );
    };

    return (
        <div className="stake-card highlight-card">
            <h2>💎 Block Staking</h2>
            <p className="stake-desc">Stake blocks to earn daily Bananium rewards.</p>

            <div className="stake-tabs">
                <button
                    className={selectedTab === 'stake' ? 'active' : ''}
                    onClick={() => setSelectedTab('stake')}
                >
                    Stake
                </button>
                <button
                    className={selectedTab === 'unstake' ? 'active' : ''}
                    onClick={() => setSelectedTab('unstake')}
                >
                    Unstake
                </button>
            </div>

            <div className="staking-content">
                {selectedTab === 'stake'
                    ? renderVoxelList('available', 'stake')
                    : renderVoxelList('staked', 'unstake')
                }
            </div>
        </div>
    );
};

export default StakeCard;
